import { useState, useRef, useEffect, useCallback } from 'react'
import './VideoTrimmer.css'

function VideoTrimmer({ videoUrl, duration, onTrimChange, onSeek }) {
  const [trimStart, setTrimStart] = useState(0)
  const [trimEnd, setTrimEnd] = useState(duration)
  const [isDragging, setIsDragging] = useState(null) // 'start', 'end', or null
  const timelineRef = useRef(null)

  // Update trimEnd when duration changes (initial load)
  useEffect(() => {
    if (duration > 0 && trimEnd === 0) {
      setTrimEnd(duration)
    }
  }, [duration, trimEnd])

  // Notify parent of trim changes
  useEffect(() => {
    onTrimChange?.({ start: trimStart, end: trimEnd })
  }, [trimStart, trimEnd, onTrimChange])

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    const ms = Math.floor((seconds % 1) * 10)
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`
  }

  const getPositionFromEvent = useCallback((e) => {
    if (!timelineRef.current || duration === 0) return 0
    
    const rect = timelineRef.current.getBoundingClientRect()
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const x = clientX - rect.left
    const percentage = Math.max(0, Math.min(1, x / rect.width))
    return percentage * duration
  }, [duration])

  const handleMouseDown = (handle) => (e) => {
    e.preventDefault()
    setIsDragging(handle)
    // Immediately seek to the handle's current position when starting to drag
    onSeek?.(handle === 'start' ? trimStart : trimEnd)
  }

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return
    
    const time = getPositionFromEvent(e)
    
    if (isDragging === 'start') {
      // Ensure start doesn't go past end (with 0.5s minimum duration)
      const maxStart = Math.max(0, trimEnd - 0.5)
      const newTime = Math.min(time, maxStart)
      setTrimStart(newTime)
      // Scrub video to show the frame at the start handle position
      onSeek?.(newTime)
    } else if (isDragging === 'end') {
      // Ensure end doesn't go before start (with 0.5s minimum duration)
      const minEnd = Math.min(duration, trimStart + 0.5)
      const newTime = Math.max(time, minEnd)
      setTrimEnd(newTime)
      // Scrub video to show the frame at the end handle position
      onSeek?.(newTime)
    }
  }, [isDragging, trimEnd, trimStart, duration, getPositionFromEvent, onSeek])

  const handleMouseUp = useCallback(() => {
    setIsDragging(null)
  }, [])

  // Global mouse/touch event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('touchmove', handleMouseMove)
      document.addEventListener('touchend', handleMouseUp)
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.removeEventListener('touchmove', handleMouseMove)
        document.removeEventListener('touchend', handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  const handleTimelineClick = (e) => {
    if (isDragging) return
    
    const time = getPositionFromEvent(e)
    
    // Determine which handle is closer and move it
    const distToStart = Math.abs(time - trimStart)
    const distToEnd = Math.abs(time - trimEnd)
    
    if (distToStart < distToEnd) {
      const newTime = Math.min(time, trimEnd - 0.5)
      setTrimStart(newTime)
      onSeek?.(newTime)
    } else {
      const newTime = Math.max(time, trimStart + 0.5)
      setTrimEnd(newTime)
      onSeek?.(newTime)
    }
  }

  const handleInputChange = (type) => (e) => {
    const value = parseFloat(e.target.value)
    if (isNaN(value)) return
    
    if (type === 'start') {
      const newTime = Math.max(0, Math.min(value, trimEnd - 0.5))
      setTrimStart(newTime)
      onSeek?.(newTime)
    } else {
      const newTime = Math.min(duration, Math.max(value, trimStart + 0.5))
      setTrimEnd(newTime)
      onSeek?.(newTime)
    }
  }

  if (!videoUrl || duration === 0) {
    return null
  }

  const startPercent = (trimStart / duration) * 100
  const endPercent = (trimEnd / duration) * 100
  const trimmedDuration = trimEnd - trimStart

  return (
    <div className="video-trimmer">
      <div className="trimmer-header">
        <span className="trimmer-title">✂️ Trim Video</span>
        <span className="trimmer-duration">
          Selected: {formatTime(trimmedDuration)} / {formatTime(duration)}
        </span>
      </div>

      <div 
        className="trimmer-timeline"
        ref={timelineRef}
        onClick={handleTimelineClick}
      >
        {/* Background track */}
        <div className="trimmer-track" />
        
        {/* Excluded regions (dimmed) */}
        <div 
          className="trimmer-excluded trimmer-excluded-start"
          style={{ width: `${startPercent}%` }}
        />
        <div 
          className="trimmer-excluded trimmer-excluded-end"
          style={{ width: `${100 - endPercent}%` }}
        />
        
        {/* Selected region */}
        <div 
          className="trimmer-selected"
          style={{ 
            left: `${startPercent}%`, 
            width: `${endPercent - startPercent}%` 
          }}
        />
        
        {/* Start handle */}
        <div 
          className={`trimmer-handle trimmer-handle-start ${isDragging === 'start' ? 'dragging' : ''}`}
          style={{ left: `${startPercent}%` }}
          onMouseDown={handleMouseDown('start')}
          onTouchStart={handleMouseDown('start')}
        >
          <div className="handle-bar" />
          <div className="handle-label">{formatTime(trimStart)}</div>
        </div>
        
        {/* End handle */}
        <div 
          className={`trimmer-handle trimmer-handle-end ${isDragging === 'end' ? 'dragging' : ''}`}
          style={{ left: `${endPercent}%` }}
          onMouseDown={handleMouseDown('end')}
          onTouchStart={handleMouseDown('end')}
        >
          <div className="handle-bar" />
          <div className="handle-label">{formatTime(trimEnd)}</div>
        </div>
      </div>

      {/* Time inputs for precise control */}
      <div className="trimmer-inputs">
        <div className="trimmer-input-group">
          <label>Start</label>
          <input
            type="number"
            min="0"
            max={trimEnd - 0.5}
            step="0.1"
            value={trimStart.toFixed(1)}
            onChange={handleInputChange('start')}
          />
          <span className="input-unit">sec</span>
        </div>
        
        <div className="trimmer-input-group">
          <label>End</label>
          <input
            type="number"
            min={trimStart + 0.5}
            max={duration}
            step="0.1"
            value={trimEnd.toFixed(1)}
            onChange={handleInputChange('end')}
          />
          <span className="input-unit">sec</span>
        </div>

        <button 
          className="trimmer-reset-btn"
          onClick={() => {
            setTrimStart(0)
            setTrimEnd(duration)
          }}
        >
          Reset
        </button>
      </div>
    </div>
  )
}

export default VideoTrimmer

